import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { errorMessage } from '../api/client';
import { IconFolder, IconRefresh } from '../components/icons';
import { daysUntil, Empty, formatDate, Spinner, timeAgo } from '../components/ui';
import { desktop, openExternal, type InstallTarget, type LicenseStatus, webStoreUrl } from '../platform';
import { useToast } from '../state/toast';
import { useAppUpdate } from '../state/update';

/** The app's version and its updates: they download by themselves, this checks right away. */
function AppUpdates() {
  const [update, setUpdate] = useAppUpdate();
  const [checking, setChecking] = useState(false);
  if (!desktop || !update) return null;

  const check = async () => {
    setChecking(true);
    try {
      setUpdate(await desktop!.updates.check());
    } finally {
      setChecking(false);
    }
  };
  const text = {
    unsupported: 'Updates are off in development builds.',
    idle: 'Checks for updates automatically.',
    checking: 'Checking for updates…',
    current: `Up to date (checked ${timeAgo(update.checkedAt)}).`,
    available: `Version ${update.version} is out. This copy can't update itself: download it and install it over this one.`,
    downloading: `Downloading version ${update.version}… ${update.percent ?? 0}%`,
    ready: `Version ${update.version} is ready: restart to update, or it updates the next time you quit the app.`,
    error: update.error ?? "Couldn't check for updates.",
  }[update.status];
  const busy = checking || update.status === 'checking' || update.status === 'downloading' || update.status === 'unsupported';

  return (
    <div className="row between wrap">
      <div style={{ minWidth: 0 }}>
        <div style={{ color: 'var(--va-headline)' }}>Version {desktop.version}</div>
        <div className="small muted">{text}</div>
      </div>
      {update.status === 'ready' ? (
        <button className="btn btn-sm btn-primary" onClick={() => void desktop!.updates.install()}>Restart now</button>
      ) : update.status === 'available' ? (
        <button className="btn btn-sm btn-primary" onClick={() => openExternal(webStoreUrl('/download'))}>Download</button>
      ) : (
        <button className="btn btn-sm btn-outline" onClick={check} disabled={busy}>
          {checking ? <Spinner /> : <IconRefresh size={14} />} Check for updates
        </button>
      )}
    </div>
  );
}

/** Desktop app settings: license status for this computer and install folders. */
export function SettingsPage() {
  const toast = useToast();
  const [status, setStatus] = useState<LicenseStatus | null>(null);
  const [targets, setTargets] = useState<InstallTarget[]>([]);
  const [startAtLogin, setStartAtLogin] = useState<boolean>(true);

  useEffect(() => {
    if (!desktop) return;
    desktop.license.status().then(setStatus);
    desktop.installs.targets().then(setTargets);
    desktop.system.getSetting<boolean>('startAtLogin').then((v) => setStartAtLogin(v !== false));
    return desktop.license.onChange(setStatus);
  }, []);

  if (!desktop) {
    return (
      <div className="container">
        <Empty title="Settings live in the app">These settings are part of the VA Plugin Store desktop app.</Empty>
      </div>
    );
  }

  const sync = async () => {
    try {
      const s = await desktop!.license.sync();
      setStatus(s);
      if (s.error) toast.error(s.error.message);
      else toast.success('Licenses refreshed.');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const days = daysUntil(status?.expiresAt);

  return (
    <div className="container" style={{ maxWidth: 900 }}>
      <div className="page-head">
        <div>
          <span className="eyebrow">VA Plugin Store app {desktop.version}</span>
          <h1>Settings</h1>
        </div>
      </div>
      <div className="stack lg">
        <div className="card stack">
          <div className="card-title-row">
            <h3 style={{ margin: 0 }}>Licenses on this computer</h3>
            <button className="btn btn-sm btn-outline" onClick={sync} disabled={status?.syncing}>
              {status?.syncing ? <Spinner /> : <IconRefresh size={14} />} Refresh licenses
            </button>
          </div>
          {!status && <Spinner />}
          {status && (
            <>
              {status.error && <div className="form-error">{status.error.message}</div>}
              <dl className="facts">
                <dt>Signed in as</dt>
                <dd>{status.email ?? 'Not signed in'}</dd>
                <dt>Last refresh</dt>
                <dd>{timeAgo(status.lastSyncAt)}</dd>
                <dt>Works offline until</dt>
                <dd>
                  {status.expiresAt ? `${formatDate(status.expiresAt, { dateStyle: 'medium', timeStyle: 'short' })} (${Math.max(0, days ?? 0)} days)` : '—'}
                </dd>
                <dt>Unlocked</dt>
                <dd>
                  {status.codes.length === 0 ? (
                    <span className="muted">Nothing yet. Products you own unlock here automatically.</span>
                  ) : (
                    <span className="tag-list">
                      {status.codes.map((c) => (
                        <span key={c.code} className={`pill ${c.source === 'trial' ? 'warning' : 'success'}`} title={c.expiresAt ? `until ${formatDate(c.expiresAt)}` : undefined}>
                          {c.code}
                        </span>
                      ))}
                    </span>
                  )}
                </dd>
                <dt>License file</dt>
                <dd className="mono small" style={{ wordBreak: 'break-all' }}>{status.licensePath}</dd>
                <dt>Machine ID</dt>
                <dd className="mono small" style={{ wordBreak: 'break-all' }}>{status.machineId ?? '—'}</dd>
              </dl>
              <p className="small dim" style={{ margin: 0 }}>
                Plugins read the license file every few seconds. Sign out or switch accounts and products this account doesn't own go back to the watermark. <Link to="/account/devices">Manage devices</Link>
              </p>
            </>
          )}
        </div>

        <div className="card stack">
          <h3 style={{ margin: 0 }}>Install folders</h3>
          <p className="small muted" style={{ margin: 0 }}>Where the app puts each kind of product, every product in its own folder. Resolume reads plugins from its Extra Effects folder when it starts.</p>
          {targets.map((t) => (
            <div key={t.kind} className="row between wrap" style={{ padding: '10px 0', borderTop: '1px solid var(--va-line)' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: 'var(--va-headline)' }}>{t.label}</div>
                <div className="mono small dim" style={{ wordBreak: 'break-all' }}>{t.path}</div>
                {t.fixed && <div className="small muted">Apps always go to your Downloads folder, each in a folder named after the product.</div>}
                {!t.detected && <div className="small" style={{ color: 'var(--va-warning)' }}>This folder doesn't exist yet: it will be created on first install.</div>}
              </div>
              {!t.fixed && (
                <div className="row" style={{ gap: 6 }}>
                  <button className="btn btn-sm btn-outline" onClick={async () => setTargets(await desktop!.installs.chooseTarget(t.kind))}>
                    <IconFolder size={14} /> Change
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={async () => setTargets(await desktop!.installs.resetTarget(t.kind))}>Default</button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="card stack">
          <h3 style={{ margin: 0 }}>App</h3>
          <AppUpdates />
          <label className="checkbox">
            <input
              type="checkbox"
              checked={startAtLogin}
              onChange={async (e) => {
                setStartAtLogin(e.target.checked);
                await desktop!.system.setSetting('startAtLogin', e.target.checked);
              }}
            />
            <span>Start in the background when I log in, so licenses stay fresh (recommended).</span>
          </label>
        </div>
      </div>
    </div>
  );
}
